import type { Print, Product } from '@/shared/api/schemas';

/**
 * Витрина заказчика, пока не приехал настоящий каталог.
 *
 * Общий демо-каталог здесь не годится: в нём сумка и кепка, а ФОСИ продаёт
 * только футболки, и одна футболка на витрине не даёт увидеть главного —
 * что ряд листается. Поэтому товаров нарочно больше, чем помещается.
 *
 * Цвета взяты разные: так на витрине видно, что карточки показывают
 * изделие в его цвете, а не одну и ту же картинку.
 *
 * NOTE: временные данные. Уходят, как только заказчик пришлёт названия,
 * цены и фотографии.
 */
const SIZES = ['XS', 'S', 'M', 'L', 'XL'];

const SPEC = {
  '1': 'Свободный крой',
  '2': 'Круглый вырез горловины из трикотажной резинки',
  '3': 'Спущенные плечи',
  '4': 'Плотность 260 гр',
  '5': 'Состав: 92% хлопок, 8% эластан',
};

// Цвета готовых футболок с принтом — то, в чём их можно купить.
//
// Белой здесь нет: на белой этот принт не печатают. Список конструктора
// другой и живёт в наборе заказчика (customColors в brand.ts) — он про
// то, на чём мы печатаем, а не про товар.
//
// NOTE: пара к infra/init-db/015_fosy_catalog_colors.sql. Боевой список
// живёт в базе, этот — запасной на случай, когда сервер не ответил.
const PALETTE = ['black', 'pink', 'navy', 'grey'];

const SHIRTS: Array<[string, string]> = [
  ['Футболка 1', 'black'],
  ['Футболка 2', 'pink'],
  ['Футболка 3', 'navy'],
  ['Футболка 4', 'grey'],
];

const FOSY: Product[] = SHIRTS.map(([title, color], index) => ({
  id: `fosy-tshirt-${index + 1}`,
  product_type: 'tshirt',
  title,
  // Выбранный цвет первым: карточка показывает изделие именно в нём.
  // Все цвета у каждого изделия: печатают на них одинаково, и урезанный
  // набор читался бы как ограничение, которого нет. Первым идёт цвет
  // самой карточки — он и выбран по умолчанию.
  colors: [color, ...PALETTE.filter((item) => item !== color)],
  sizes: SIZES,
  // Цена одна на все футболки и одна со всеми местами, где она
  // показывается: витрина, конструктор, корзина, мини-приложение.
  //
  // NOTE: пара к infra/init-db/011_fosy_price_2700.sql. Боевая цена
  // живёт в базе, этот файл — запасной каталог на случай, когда сервер
  // не ответил. Разойдутся — человек увидит одну цену при живом сервере
  // и другую при упавшем, и поймать это можно будет только случайно.
  price_rub: 2700,
  spec: SPEC,
}));

/**
 * Витрина СИЯЙ.
 *
 * Здесь не только футболки: заказчик продаёт четыре вида вещей, и витрина
 * показывает их все. Собрать своё при этом можно пока только на футболке —
 * это другой список, `customGarments` в brand.ts.
 *
 * Цена и размерный ряд зависят от вида вещи: у кепки и шопера размера
 * нет вовсе, и шаг с выбором для них пропускается.
 *
 * NOTE: временные данные. Уходят, как только заказчик пришлёт настоящие
 * названия, цены и снимки. Худи в списке нет намеренно: мокапов для него
 * у нас нет, и карточка показала бы пустое место.
 */
const SIYAI_SIZES = ['S', 'M', 'L', 'XL', 'XXL'];

const SIYAI_SPEC = {
  '1': 'Оверсайз, спущенное плечо',
  '2': 'Плотность 260 гр',
  '3': 'Состав: 92% хлопок, 8% эластан',
  '4': 'Нанесение: прямая печать',
};

const SIYAI_TEES: Array<[string, string]> = [
  ['Футболка Oversize', 'black'],
  ['Футболка Oversize', 'graphite'],
  ['Футболка Oversize', 'beige'],
  ['Футболка Oversize', 'grey'],
];

const SIYAI: Product[] = [
  ...SIYAI_TEES.map(([title, color], index) => ({
    id: `siyai-tshirt-${index + 1}`,
    product_type: 'tshirt',
    title,
    colors: [color, ...['black', 'graphite', 'grey', 'beige'].filter((item) => item !== color)],
    sizes: SIYAI_SIZES,
    price_rub: 3000,
    spec: SIYAI_SPEC,
  })),
  {
    id: 'siyai-cap-1',
    product_type: 'cap',
    title: 'Кепка Classic',
    colors: ['beige'],
    // Размера нет: кепка одна на всех, и пустой выбор размера читался бы
    // как недоделка.
    sizes: [],
    price_rub: 2200,
    spec: { '1': 'Регулируемый ремешок', '2': 'Состав: 100% хлопок' },
  },
  {
    id: 'siyai-tote-1',
    product_type: 'tote',
    title: 'Шопер',
    colors: ['black'],
    sizes: [],
    price_rub: 1500,
    spec: { '1': 'Длинные ручки', '2': 'Состав: 100% хлопок' },
  },
];

const CATALOGS: Record<string, Product[]> = {
  fosy: FOSY,
  siyai: SIYAI,
};

/** Витрина заказчика на время разработки. Пусто — берётся общее демо. */
export function demoCatalog(slug: string): Product[] | null {
  return CATALOGS[slug] ?? null;
}

/**
 * Библиотека готовых принтов СИЯЙ.
 *
 * Общая демо-библиотека здесь не годится: в ней принты артистов
 * effective-records, подписанные их именами, и в чужом боте они читаются
 * как чужой товар.
 *
 * Файлы — те четыре, что у нас есть на самом деле. Выдумывать строки
 * под картинки, которых нет, нельзя: в библиотеке появятся битые плитки,
 * и понять, что виноваты данные, а не экран, будет нельзя.
 *
 * NOTE: ждём от заказчика настоящую библиотеку. На макете её семь штук.
 */
const SIYAI_PRINTS: Print[] = [
  ['siyai-print-1', 'Призрак', '/demo/print-ghost.png'],
  ['siyai-print-2', 'Сияй', '/demo/print-lets-shine.png'],
  ['siyai-print-3', 'Контур', '/demo/print-oneil.png'],
  ['siyai-print-4', 'Орбита', '/demo/print-rompasso.png'],
].map(([id, title, image_url]) => ({
  id: id as string,
  title: title as string,
  image_url: image_url as string,

  // Автора и коллекции у этих принтов нет: они не чьи-то, а магазина.
  // Подпись под плиткой осталась бы пустой строкой, и это честнее,
  // чем выдуманное имя.
  artist: null,
  collection: null,

  // Настоящие пропорции файлов. Соврать здесь — значит получить пустые
  // поля вокруг рисунка: рамка принта строится по этим числам.
  source_width: 1024,
  source_height: 1024,
}));

const PRINT_LIBRARIES: Record<string, Print[]> = {
  siyai: SIYAI_PRINTS,
};

/** Библиотека принтов заказчика. Пусто — берётся общая демо-библиотека. */
export function demoPrints(slug: string): Print[] | null {
  return PRINT_LIBRARIES[slug] ?? null;
}
